import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Building2,
  Compass,
  FileText,
  Inbox,
  Smartphone,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import {
  NOMBRE_ACCION,
  tiempo,
  useCorreoWeb,
  useGuardarCorreoWeb,
  useProbarCorreoWeb,
  useTuWeb,
  type FilaLista,
  type Periodo,
  type TuWeb,
} from '../api/web';
import { AyudaGuia, Pregunta } from '../components/Ayuda';
import AvisosCorreo from '../components/AvisosCorreo';

// Cómo va habitania.info, para alguien que nunca ha abierto Google Analytics:
// pocas cifras, con su nombre de siempre, una frase que las cuenta y la
// explicación al lado. Solo totales: la medición es anónima (ver web.py).

const PERIODOS: { v: Periodo; label: string }[] = [
  { v: 7, label: '7 días' },
  { v: 30, label: '30 días' },
  { v: 90, label: '3 meses' },
  { v: 365, label: '12 meses' },
];

const DISPOSITIVO: Record<string, string> = { movil: 'Móvil', ordenador: 'Ordenador', tablet: 'Tableta', otro: 'Otros' };

const numero = (n: number) => n.toLocaleString('es-ES');
const pct = (n: number, total: number) => (total ? Math.round((n * 100) / total) : 0);

const regiones = (() => {
  try {
    return new Intl.DisplayNames(['es'], { type: 'region' });
  } catch {
    return null;
  }
})();
const pais = (codigo: string) => regiones?.of(codigo) ?? codigo;

const fechaLarga = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
};
const etiquetaCorta = (iso: string, periodo: Periodo) => {
  const [y, m, d] = iso.split('-').map(Number);
  if (periodo === 365) return new Date(y, m - 1, 1).toLocaleDateString('es-ES', { month: 'short' }).replace('.', '');
  if (periodo === 7) return new Date(y, m - 1, d).toLocaleDateString('es-ES', { weekday: 'short' }).replace('.', '');
  return String(d);
};
// Mayúscula solo la primera letra: «Jueves, 1 de octubre», no «Jueves, 1 De Octubre».
const mayuscula = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
const etiquetaLarga = (iso: string, periodo: Periodo) => {
  if (periodo !== 365) return mayuscula(fechaLarga(iso));
  const [y, m] = iso.split('-').map(Number);
  return mayuscula(new Date(y, m - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }));
};

export default function TuWebView() {
  const [periodo, setPeriodo] = useState<Periodo>(30);
  const { data, isLoading, isError, error, refetch, isFetching } = useTuWeb(periodo);

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-deep">Tu web</h1>
        <AyudaGuia seccion="tu-web" />
      </div>
      <p className="mt-1 max-w-2xl text-sm text-ink/55">
        Cómo va habitania.info: cuánta gente entra, cuánto se queda, de dónde llega, qué mira y
        qué te trae.
      </p>
      <Pregunta titulo="¿Qué es una visita?">
        Una persona que entra en la web y mira una o varias páginas. Si pasa más de media hora sin
        abrir ninguna y vuelve, cuenta como otra visita. El tiempo es el que tiene la página delante:
        si cambia de pestaña, el reloj se para.
      </Pregunta>
      <Pregunta titulo="¿Puedo saber quién me visita?">
        No, y es a propósito. La web cuenta las visitas sin cookies y sin guardar nada que diga quién
        es cada uno; por eso no necesita el aviso de cookies que tienen otras webs. Quién es alguien
        lo sabes cuando te deja sus datos: entonces aparece en Clientes, en «Solicitudes».
      </Pregunta>
      <Pregunta titulo="¿Por qué la web no tiene aviso de cookies?">
        Porque no usa cookies de publicidad ni de nadie más, y las visitas se cuentan con una
        herramienta propia y anónima. La Agencia Española de Protección de Datos permite medir así sin
        pedir permiso. Lo explica la política de cookies de la web, que además deja a cualquiera pedir
        que no le contemos.
      </Pregunta>
      <Pregunta titulo="¿Cuentan mis propias visitas?">
        Sí, como las de cualquiera, salvo que lo evites: en Ajustes, «No contar mis visitas», una vez
        en cada navegador que uses (el del ordenador y el del móvil).
      </Pregunta>

      <div className="mt-5 flex flex-wrap items-center gap-2" role="group" aria-label="Periodo">
        {PERIODOS.map((p) => (
          <button
            key={p.v}
            type="button"
            onClick={() => setPeriodo(p.v)}
            aria-pressed={periodo === p.v}
            className={clsx(
              'rounded-full px-4 py-1.5 text-sm font-bold transition',
              periodo === p.v ? 'bg-deep text-white' : 'border border-deep/15 text-ink/65 hover:border-brand hover:text-brand',
            )}
          >
            {p.label}
          </button>
        ))}
        {isFetching && !isLoading && <span className="text-xs text-ink/45">Actualizando…</span>}
      </div>

      {isLoading ? (
        <p className="mt-10 text-sm text-ink/50">Cargando…</p>
      ) : isError || !data ? (
        <div className="mt-8 rounded-2xl border border-dashed border-red-300 bg-red-50 px-6 py-12 text-center">
          <AlertTriangle className="mx-auto text-red-500" size={28} strokeWidth={1.5} />
          <p className="mt-3 font-bold text-red-700">No se han podido cargar las visitas</p>
          <p className="mt-1 text-sm text-red-600/80">{(error as Error)?.message}</p>
          <button onClick={() => refetch()} className="mt-4 rounded-full bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700">
            Reintentar
          </button>
        </div>
      ) : (
        <Contenido d={data} />
      )}

      <CorreoSemanal />
    </div>
  );
}

function Contenido({ d }: { d: TuWeb }) {
  const total = d.kpis.visitas;
  const desdeInicio = d.serie.length < d.periodo && d.periodo !== 365;

  return (
    <>
      {d.en_obras && (
        <div className="mt-4 flex gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <p>
            La web todavía pide clave, así que lo que ves aquí son pruebas. El día que se abra, la
            cuenta empieza de cero.
          </p>
        </div>
      )}

      <p className="mt-5 rounded-2xl bg-soft px-5 py-4 text-base leading-relaxed text-deep">{d.frase}</p>
      {desdeInicio && (
        <p className="mt-2 text-xs text-ink/50">
          Las visitas se cuentan desde el {fechaLarga(d.inicio)}: antes de esa fecha no hay datos.
        </p>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Visitas" valor={numero(total)} actual={d.kpis.visitas} anterior={d.kpis_anterior?.visitas} />
        <Kpi label="Tiempo medio por visita" valor={tiempo(d.kpis.tiempo_medio_s)}
          actual={d.kpis.tiempo_medio_s} anterior={d.kpis_anterior?.tiempo_medio_s} />
        <Kpi label="Contactos" valor={numero(d.kpis.contactos)} actual={d.kpis.contactos} anterior={d.kpis_anterior?.contactos} />
        <Kpi label="Contactos por cada 100 visitas" valor={d.kpis.por_cien === null ? '—' : d.kpis.por_cien.toLocaleString('es-ES')}
          actual={d.kpis.por_cien} anterior={d.kpis_anterior?.por_cien} />
      </div>
      {!d.kpis_anterior && (
        <p className="mt-2 text-xs text-ink/45">
          Sin comparación todavía: hace falta un periodo anterior entero medido.
        </p>
      )}

      <Serie d={d} />

      <div className="grid gap-x-6 lg:grid-cols-2">
        <Bloque
          icono={Compass}
          titulo="De dónde llegan"
          pregunta={{
            titulo: '¿Por qué tantas salen como «directo»?',
            texto: (
              <>
                Cuando alguien escribe la dirección, la tiene guardada o abre un enlace que le pasaron
                por WhatsApp, por correo o en un PDF, su navegador no dice de dónde viene. Para saber
                cuánta gente te trae un anuncio, un folleto o una publicación, pídele a Adrien un enlace
                con etiqueta de campaña: esas visitas saldrán con su nombre en «Campañas».
              </>
            ),
          }}
        >
          {d.fuentes.length === 0 ? (
            <Vacio>Todavía no hay visitas en este periodo.</Vacio>
          ) : (
            <Barras filas={d.fuentes.map((f) => ({ nombre: f.nombre, n: f.visitas }))} total={total} />
          )}
          <Detalle titulo="Campañas" filas={d.campanas} otros={d.campanas_otras} />
          <Detalle titulo="Otras webs" filas={d.webs} otros={d.webs_otras} />
        </Bloque>

        <Bloque icono={Smartphone} titulo="Con qué entran y desde dónde">
          {total === 0 ? (
            <Vacio>Todavía no hay visitas en este periodo.</Vacio>
          ) : (
            <>
              <Barras
                filas={Object.entries(d.dispositivos)
                  .filter(([, n]) => n)
                  .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
                  .map(([k, n]) => ({ nombre: DISPOSITIVO[k] ?? k, n: n ?? 0 }))}
                total={total}
              />
              <p className="mt-5 text-xs font-bold uppercase tracking-wider text-ink/45">Países</p>
              {d.paises.length === 0 && d.paises_otros === 0 ? (
                <p className="mt-2 text-sm text-ink/55">Sin datos de país.</p>
              ) : (
                <Barras
                  filas={[
                    ...d.paises.map((p) => ({ nombre: pais(p.clave), n: p.visitas })),
                    ...(d.paises_otros ? [{ nombre: 'Otros países', n: d.paises_otros }] : []),
                  ]}
                  total={total}
                />
              )}
            </>
          )}
        </Bloque>
      </div>

      <Bloque icono={FileText} titulo="Qué miran">
        {d.paginas.length === 0 ? (
          <Vacio>Todavía no hay páginas vistas en este periodo.</Vacio>
        ) : (
          <Tabla
            cabecera={['Página', 'Veces vista', 'Tiempo medio']}
            filas={d.paginas.map((p) => [p.nombre, numero(p.vistas), tiempo(p.tiempo_medio_s)])}
          />
        )}
      </Bloque>

      <Bloque icono={Building2} titulo="Las viviendas más vistas">
        {d.viviendas.length === 0 ? (
          <Vacio>Nadie ha abierto todavía la ficha de una vivienda en este periodo.</Vacio>
        ) : (
          <ul className="divide-y divide-deep/10">
            {d.viviendas.map((v) => {
              const clics = Object.entries(v.acciones).filter(([, n]) => n);
              return (
                <li key={v.ruta} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-deep">{v.nombre.replace(/^Vivienda: /, '')}</p>
                    <p className="text-xs text-ink/55">
                      {numero(v.vistas)} {v.vistas === 1 ? 'vez' : 'veces'}
                      {v.tiempo_medio_s ? ` · ${tiempo(v.tiempo_medio_s)} de media` : ''}
                      {clics.length > 0 && ' · ' + clics.map(([k, n]) => `${n} «${NOMBRE_ACCION[k as keyof typeof NOMBRE_ACCION]}»`).join(' · ')}
                      {v.en_la_web === false && ' · ya no está en la web'}
                    </p>
                  </div>
                  {v.property_id && v.nombre !== 'Vivienda borrada del CRM' && (
                    <Link
                      to={`/viviendas?ficha=${encodeURIComponent(v.property_id)}`}
                      className="rounded-full border border-deep/15 px-3 py-1 text-xs font-bold text-ink/65 hover:border-brand hover:text-brand"
                    >
                      Ver ficha
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Bloque>

      <Bloque
        icono={Inbox}
        titulo="Lo que la web te ha traído"
        pregunta={{
          titulo: '¿Qué cuenta como contacto?',
          texto: (
            <>
              Una persona que te deja sus datos desde la web: los formularios («Te llamamos», pedir un
              servicio, inversores) y las cuentas nuevas. Los clics en WhatsApp o en llamar van aparte:
              se sabe que pulsaron, no si llegaron a escribirte. Las peticiones de clientes desde su
              área privada tampoco suman: ya eran clientes.
            </>
          ),
        }}
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Mini label="Solicitudes desde la web" valor={d.contactos.web} />
          <Mini label="Cuentas nuevas" valor={d.contactos.cuentas} />
          <Mini label="Estudios de factura" valor={d.contactos.estudios} />
          <Mini label="Clics en WhatsApp" valor={d.acciones.whatsapp ?? 0} />
          <Mini label="Clics en llamar" valor={d.acciones.telefono ?? 0} />
          <Mini label="Peticiones de tus clientes" valor={d.contactos.portal} />
        </div>
        <div className="mt-5 grid gap-6 sm:grid-cols-2">
          {d.contactos.por_tipo.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-ink/45">Qué piden</p>
              <Barras filas={d.contactos.por_tipo} total={d.contactos.web} />
            </div>
          )}
          {d.contactos.por_pagina.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-ink/45">Desde qué página</p>
              <Barras filas={d.contactos.por_pagina} total={d.contactos.web} />
            </div>
          )}
        </div>
      </Bloque>
    </>
  );
}

function Serie({ d }: { d: TuWeb }) {
  const [elegido, setElegido] = useState<string | null>(null);
  const max = Math.max(1, ...d.serie.map((s) => s.visitas));
  const actual = d.serie.find((s) => s.etiqueta === elegido) ?? d.serie[d.serie.length - 1];
  // Con 30 o 90 días no caben todas las etiquetas: una de cada pocas.
  const cada = d.periodo === 90 ? 15 : d.periodo === 30 ? 5 : 1;

  return (
    <section className="mt-6 rounded-2xl border border-deep/10 bg-surface p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-wider text-ink/45">
          Visitas {d.periodo === 365 ? 'por mes' : 'por día'}
        </p>
        {actual && (
          <p className="text-sm text-deep">
            {etiquetaLarga(actual.etiqueta, d.periodo)}:{' '}
            <b>{numero(actual.visitas)}</b> {actual.visitas === 1 ? 'visita' : 'visitas'}
          </p>
        )}
      </div>
      <div className="mt-3 flex h-36 items-end gap-1 border-b border-deep/10" role="list">
        {d.serie.map((s) => (
          <button
            key={s.etiqueta}
            role="listitem"
            type="button"
            onClick={() => setElegido(s.etiqueta)}
            onMouseEnter={() => setElegido(s.etiqueta)}
            onFocus={() => setElegido(s.etiqueta)}
            title={`${etiquetaLarga(s.etiqueta, d.periodo)}: ${numero(s.visitas)}`}
            aria-label={`${etiquetaLarga(s.etiqueta, d.periodo)}: ${numero(s.visitas)} visitas`}
            className="group flex h-full flex-1 flex-col items-center justify-end"
          >
            <span
              className={clsx(
                'w-full max-w-10 rounded-t-md transition-colors',
                s.etiqueta === actual?.etiqueta ? 'bg-deep' : 'bg-brand group-hover:bg-deep/80',
              )}
              style={{ height: `${Math.max(2, (s.visitas / max) * 100)}%` }}
            />
          </button>
        ))}
      </div>
      <div className="mt-1 flex gap-1">
        {d.serie.map((s, i) => (
          <span key={s.etiqueta} className="flex-1 text-center text-[0.62rem] text-ink/45">
            {(d.serie.length - 1 - i) % cada === 0 ? etiquetaCorta(s.etiqueta, d.periodo) : ''}
          </span>
        ))}
      </div>
    </section>
  );
}

// ── Piezas ────────────────────────────────────────────────────────

function Kpi({ label, valor, actual, anterior }: {
  label: string;
  valor: string;
  actual: number | null;
  anterior: number | null | undefined;
}) {
  const cambio = actual !== null && anterior ? Math.round(((actual - anterior) * 100) / anterior) : null;
  return (
    <div className="rounded-2xl border border-deep/10 bg-surface px-4 py-3">
      <p className="text-xl font-extrabold text-deep">{valor}</p>
      <p className="text-[0.68rem] font-bold uppercase tracking-wider text-ink/45">{label}</p>
      {cambio !== null && (
        <p className="mt-1 flex items-center gap-1 text-xs text-ink/60">
          {cambio > 0 ? <ArrowUp size={12} /> : cambio < 0 ? <ArrowDown size={12} /> : null}
          {cambio === 0 ? 'Igual que el periodo anterior' : `${Math.abs(cambio)} % ${cambio > 0 ? 'más' : 'menos'} que el periodo anterior`}
        </p>
      )}
    </div>
  );
}

function Mini({ label, valor }: { label: string; valor: number }) {
  return (
    <div className="rounded-xl bg-soft px-3 py-2.5">
      <p className="text-lg font-extrabold text-deep">{numero(valor)}</p>
      <p className="text-xs text-ink/60">{label}</p>
    </div>
  );
}

function Barras({ filas, total }: { filas: { nombre: string; n: number }[]; total: number }) {
  return (
    <ul className="mt-2 space-y-2.5">
      {filas.map((f) => (
        <li key={f.nombre}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate text-deep">{f.nombre}</span>
            <span className="shrink-0 text-ink/60">
              {numero(f.n)} <span className="text-ink/40">· {pct(f.n, total)} %</span>
            </span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-soft">
            <div className="h-1.5 rounded-full bg-brand" style={{ width: `${Math.max(2, pct(f.n, total))}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Detalle({ titulo, filas, otros }: { titulo: string; filas: FilaLista[]; otros: number }) {
  if (filas.length === 0 && otros === 0) return null;
  return (
    <div className="mt-4 rounded-xl bg-soft px-3 py-2.5 text-sm">
      <p className="text-xs font-bold uppercase tracking-wider text-ink/45">{titulo}</p>
      <ul className="mt-1 space-y-0.5 text-ink/70">
        {filas.map((f) => (
          <li key={f.clave} className="flex justify-between gap-3">
            <span className="truncate">{f.nombre}</span>
            <span>{numero(f.visitas)}</span>
          </li>
        ))}
        {otros > 0 && (
          <li className="flex justify-between gap-3 text-ink/50">
            <span>Otras, con muy pocas visitas</span>
            <span>{numero(otros)}</span>
          </li>
        )}
      </ul>
    </div>
  );
}

function Tabla({ cabecera, filas }: { cabecera: string[]; filas: string[][] }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-deep/10 text-left text-xs font-bold uppercase tracking-wider text-ink/45">
          {cabecera.map((c, i) => (
            <th key={c} className={clsx('pb-2 font-bold', i > 0 && 'text-right')}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-deep/5">
        {filas.map((f) => (
          <tr key={f[0]}>
            {f.map((v, i) => (
              <td key={i} className={clsx('py-2', i === 0 ? 'text-deep' : 'text-right text-ink/65')}>{v}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Bloque({ icono: Icono, titulo, pregunta, children }: {
  icono: LucideIcon;
  titulo: string;
  pregunta?: { titulo: string; texto: ReactNode };
  children: ReactNode;
}) {
  return (
    <section className="mt-6 rounded-2xl border border-deep/10 bg-surface p-5">
      <div className="mb-4">
        <h2 className="flex items-center gap-2 text-base font-extrabold text-deep">
          <Icono size={18} className="text-brand" />
          {titulo}
        </h2>
        {pregunta && <Pregunta titulo={pregunta.titulo}>{pregunta.texto}</Pregunta>}
      </div>
      {children}
    </section>
  );
}

function Vacio({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-2xl border border-dashed border-deep/20 px-6 py-8 text-center text-sm text-ink/60">
      {children}
    </p>
  );
}

function CorreoSemanal() {
  const { data } = useCorreoWeb();
  const guardar = useGuardarCorreoWeb();
  const probar = useProbarCorreoWeb();
  return (
    <AvisosCorreo
      titulo="Tu web esta semana, por correo"
      interruptor="Enviar el resumen cada lunes"
      boton="Guardar"
      avisos={data}
      guardar={guardar}
      probar={probar}
    >
      Cada lunes a primera hora, un correo corto con las visitas de la semana, de dónde llegaron,
      las viviendas más vistas y lo que te ha traído la web.
    </AvisosCorreo>
  );
}
